import { Controller } from "@hotwired/stimulus"

/**
 * Tracks form changes and warns users before navigating away with unsaved work.
 * Works with both regular form inputs and TinyMCE editors.
 */
export default class extends Controller {
  static values = {
    message: { type: String, default: "You have unsaved changes. Are you sure you want to leave?" }
  }

  connect() {
    this.isSubmitting = false
    this.initialSnapshot = null

    // Wait for TinyMCE to initialize, then capture initial state
    this.captureInitialStateWithRetry()

    // Track form submission to allow navigation
    this.element.addEventListener("submit", this.handleSubmit.bind(this))

    // Set up beforeunload listener for browser navigation/refresh
    this.beforeUnloadHandler = this.handleBeforeUnload.bind(this)
    window.addEventListener("beforeunload", this.beforeUnloadHandler)

    // Set up Turbo navigation listener
    this.turboBeforeVisitHandler = this.handleTurboBeforeVisit.bind(this)
    document.addEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.beforeUnloadHandler)
    document.removeEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
  }

  captureInitialStateWithRetry(attempts = 0) {
    // Wait until TinyMCE editors are initialized (max 10 attempts = 5 seconds) 
    if (attempts < 10) {
      const tinymceReady = this.areTinyMCEEditorsReady()
      if (!tinymceReady) {
        setTimeout(() => this.captureInitialStateWithRetry(attempts + 1), 500)
        return
      }
    }
    console.log("TinyMCE editors are ready, capturing initial state...")
    this.initialSnapshot = this.captureCurrentState()
  }

  areTinyMCEEditorsReady() {
    // Find all textareas with tinymce controller in this form
    const tinymceTextareas = this.element.querySelectorAll("textarea[data-controller*='tinymce']")
    if (tinymceTextareas.length === 0) {
      return true // No TinyMCE editors in this form
    }

    // Check if all editors are initialized
    if (!window.tinymce || !window.tinymce.editors) {
      return false
    }

    for (const textarea of tinymceTextareas) {
      const editor = window.tinymce.get(textarea.id)
      if (!editor || !editor.initialized) {
        return false
      }
    }

    return true
  }

  captureCurrentState() {
    const state = {}

    // Capture regular form fields
    const formData = new FormData(this.element)
    for (const [name, value] of formData.entries()) {
      // Skip file inputs as they can't be compared reliably
      if (value instanceof File) continue
      state[name] = value
    }

    // Capture TinyMCE editor contents
    if (window.tinymce && window.tinymce.editors) {
      const tinymceTextareas = this.element.querySelectorAll("textarea[data-controller*='tinymce']")
      for (const textarea of tinymceTextareas) {
        const editor = window.tinymce.get(textarea.id)
        if (editor) {
          state[`_tinymce_${textarea.id}`] = editor.getContent()
        }
      }
    }

    return state
  }

  hasUnsavedChanges() {
    if (!this.initialSnapshot) {
      return false
    }

    const currentState = this.captureCurrentState()

    // Compare initial and current state
    const allKeys = new Set([...Object.keys(this.initialSnapshot), ...Object.keys(currentState)])
    
    for (const key of allKeys) {
      const initial = this.initialSnapshot[key] || ""
      const current = currentState[key] || ""
      if (initial !== current) {
        return true
      }
    }

    return false
  }

  handleSubmit() {
    // Allow form submission without warning
    this.isSubmitting = true
  }

  handleBeforeUnload(event) {
    if (!this.isSubmitting && this.hasUnsavedChanges()) {
      event.preventDefault()
      // Modern browsers ignore custom messages, but we still need to set returnValue
      event.returnValue = this.messageValue
      return this.messageValue
    }
  }

  handleTurboBeforeVisit(event) {
    if (!this.isSubmitting && this.hasUnsavedChanges()) {
      if (!window.confirm(this.messageValue)) {
        event.preventDefault()
      }
    }
  }
}
